import React, { FC, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import styles from './HeroSection.module.css';

export interface HeroSectionProps extends HeroImageProps {
    category: string;
    title: string;
    subtitle?: string;
    description?: (string | JSX.Element)[];
    link: {
        label: string;
        url: string;
    };
    titleTagVariant?: TypographyTagVariants;
    footerNote?: string;
    theme?: 'dark' | 'light';
}

export const HeroSection: FC<HeroSectionProps> = ({
    mode,
    category,
    title,
    subtitle,
    description,
    desktopImg,
    mobileImg,
    link,
    footerNote,
    theme,
    titleTagVariant,
}) => {
    const themeColor = theme === 'dark' ? 'light' : 'dark';

    return (
        <div className={`${styles['hero-section']} position-relative`} data-testid="hero-section">
            <HeroImage mode={mode} title={title} desktopImg={desktopImg} mobileImg={mobileImg} />

            <Container classes={`${styles['text-over']} ${mode === 'boxed' ? styles['text-over-boxed'] : ''}`}>
                <div className="text-start">
                    <Typography
                        classes="mb-2 mb-sm-0"
                        color={themeColor}
                        fontSize="10"
                        breakpointsStyles={{ sm: { fontSize: '12' } }}
                        styles={{ spacedText: true }}>
                        {category}
                    </Typography>
                    <Typography
                        tagVariant={titleTagVariant ?? TypographyTagVariants.p}
                        classes="lh-sm mb-3"
                        color={themeColor}
                        appearanceLevel={TypographyAppearanceVariants.level1}>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
                    </Typography>
                    {subtitle && (
                        <Typography
                            tagVariant={TypographyTagVariants.span}
                            classes={`${styles.subtitle} d-block mb-3`}
                            color={themeColor}>
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subtitle) }} />
                        </Typography>
                    )}
                    {description && description.length > 0 && (
                        <div className="mb-3">
                            {description.map((line) => (
                                <Typography classes="mb-1" color={themeColor} fontSize="16">
                                    {line}
                                </Typography>
                            ))}
                        </div>
                    )}
                    <div className="pt-3">
                        <Anchor
                            href={link.url}
                            mode={AnchorMode.secondary}
                            classes="me-md-2"
                            textColor={theme === 'dark' ? 'light' : undefined}>
                            {link.label}
                        </Anchor>
                    </div>
                </div>
            </Container>

            {footerNote && (
                <Container classes={`${styles['footer-over']} py-2 py-lg-4`}>
                    <Typography color={themeColor} classes="pb-2 my-2">
                        {footerNote}
                    </Typography>
                </Container>
            )}
        </div>
    );
};

interface HeroImageProps {
    mode: 'boxed' | 'wide';
    title: string;
    desktopImg?: string;
    mobileImg?: string;
}

const HeroImage: FC<HeroImageProps> = ({ mode, title, desktopImg, mobileImg }) => {
    const [wideHeight, setWideHeight] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            const headerHeight = document.querySelector('#sticky-header')?.clientHeight || 0;
            setWideHeight(window.innerHeight - headerHeight);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const imageHeight = mode === 'wide' ? `${wideHeight}px` : undefined;

    return (
        <>
            {(desktopImg || mobileImg) && (
                <picture>
                    {desktopImg && mobileImg ? (
                        <>
                            <source media="(min-width: 768px)" srcSet={desktopImg} style={{ height: imageHeight }} />
                            <img src={mobileImg} alt={title} style={{ height: imageHeight }} />
                        </>
                    ) : (
                        <img src={desktopImg || mobileImg} alt={title} style={{ height: imageHeight }} />
                    )}
                </picture>
            )}
        </>
    );
};
